import { useState, useEffect, useCallback, useRef } from 'react';
import { productsService } from '@/services/products.service';
import { useNetworkStore } from '@/stores/use-network-store';

/** Per-product recipe availability, keyed by product id. */
export interface RecipeAvailabilityMap {
  [productId: string]: { available: number; limitingIngredient: string };
}

/**
 * #308 — Recipe-based availability for the catalog currently on screen.
 *
 * Fetches GET /products/availability for the given product ids so the billing
 * page can block add-to-cart (and mark grid tiles out-of-stock) when an
 * ingredient shortage means the item can't be prepared — instead of the
 * cashier finding out at payment time.
 *
 * No-ops (returns an empty map) when `enabled` is false or the POS is
 * offline: without fresh data we don't block anything, and the server-side
 * deductStock() check at bill completion remains the safety net.
 */
export function useRecipeAvailability(productIds: string[], enabled: boolean) {
  const [availability, setAvailability] = useState<RecipeAvailabilityMap>({});
  const isConnected = useNetworkStore((s) => s.isConnected);

  // Key on the joined ids so a new array instance with the same ids doesn't
  // refetch every render.
  const idsKey = productIds.join(',');
  const idsRef = useRef(productIds);
  idsRef.current = productIds;

  const refresh = useCallback(async () => {
    const ids = idsRef.current;
    if (!enabled || !isConnected || ids.length === 0) {
      setAvailability({});
      return;
    }
    try {
      const entries = await productsService.getAvailability(ids);
      const map: RecipeAvailabilityMap = {};
      for (const e of entries) {
        map[e.product_id] = { available: e.available, limitingIngredient: e.limiting_ingredient };
      }
      setAvailability(map);
    } catch {
      // Availability is advisory — on failure keep the last known map rather
      // than blocking or unblocking anything abruptly.
    }
  }, [enabled, isConnected]);

  useEffect(() => {
    void idsKey; // refetch when the visible catalog changes
    refresh();
  }, [idsKey, refresh]);

  return { availability, refresh };
}
